import { getByPath, setByPath } from "../../../../dot-notation/index.js";
import { produce } from "../../../../immer/index.js";
import { createProperty } from "../../utils/createProperty.js";
function createFieldApi(store, getLogger, getSubscribersApi, refreshFormStatus, fieldMutations, fieldTransforms, validatorsById, validatorIdByFunction, validatorsByField, fieldSubscribers, errorsByField, getPrivateFieldApi) {
  const { notifyFieldSubscriber, notifyFormSubscribers, notifyMetaSubscribers } = getSubscribersApi();
  const { tryToCreateField } = getPrivateFieldApi();
  const fieldApi = {
    registerField: (fieldName, field) => {
      if (store.fields[fieldName]) return false;
      const log = getLogger();
      log("registerField", fieldName, field);
      store.fields = produce(store.fields, (draft) => {
        const draftKey = fieldName;
        let fieldResetValue;
        try {
          fieldResetValue = getByPath(store.initialValues, fieldName);
        } catch (e) {
        }
        if (fieldResetValue === void 0) {
          fieldResetValue = field.value;
        }
        draft[draftKey] = structuredClone(field);
        const draftField = draft[draftKey];
        draftField.resetValue ??= fieldResetValue;
        draftField.visible ??= true;
        draftField.disabled ??= false;
        draftField.required ??= false;
        draftField.validating ??= false;
        draftField.status ??= "pristine";
      });
      store.values = produce(store.values, (draft) => {
        const fieldPath = fieldName;
        const val = store.fields[fieldName]?.value;
        if (val !== void 0) {
          try {
            setByPath(draft, fieldPath, val);
          } catch (e) {
            createProperty(draft, fieldPath, val);
          }
        }
      });
      notifyFieldSubscriber(fieldName);
      refreshFormStatus();
      notifyMetaSubscribers();
      notifyFormSubscribers();
      return true;
    },
    deregisterField: (fieldName, destroy = false) => {
      if (destroy) {
        for (const subscription of fieldSubscribers) {
          const [field] = subscription;
          if (field === fieldName) {
            fieldSubscribers.delete(subscription);
          }
        }
        const validators = validatorsByField.get(fieldName);
        if (validators) {
          for (const validator of validators) {
            const id = validatorIdByFunction.get(validator);
            if (id) validatorsById.delete(id);
            validatorIdByFunction.delete(validator);
          }
        }
        validatorsByField.delete(fieldName);
        fieldMutations.delete(fieldName);
        fieldTransforms.delete(fieldName);
      }
      store.fields = produce(store.fields, (draft) => {
        const draftFieldName = fieldName;
        delete draft[draftFieldName];
      });
      store.errors = produce(store.errors, (draft) => {
        for (const [errorId, error] of Object.entries(draft)) {
          if (error.field === fieldName) {
            delete draft[errorId];
          }
        }
      });
      errorsByField.delete(fieldName);
      refreshFormStatus();
      notifyMetaSubscribers();
      notifyFormSubscribers();
    },
    getField: (fieldName) => {
      const field = store.fields[fieldName];
      if (field === void 0) {
        const log = getLogger();
        log("getField", `Trying to access an unknown field: ${String(fieldName)}.`);
      }
      return field;
    },
    getFields: () => {
      return Object.freeze(structuredClone(store.fields));
    },
    setFieldDisabledState: (fieldName, nextDisabled) => {
      const log = getLogger();
      log("setFieldDisabledState", fieldName, nextDisabled);
      const field = tryToCreateField(fieldName);
      const prevDisabled = field.disabled;
      store.fields = produce(store.fields, (draft) => {
        const target = draft[fieldName];
        target.disabled = nextDisabled;
      });
      if (prevDisabled !== nextDisabled) {
        notifyFieldSubscriber(fieldName);
        notifyFormSubscribers();
      }
    },
    setFieldVisibleState: (fieldName, nextVisible) => {
      const log = getLogger();
      log("setFieldVisibleState", fieldName, nextVisible);
      const field = tryToCreateField(fieldName);
      const prevVisible = field.visible;
      store.fields = produce(store.fields, (draft) => {
        const target = draft[fieldName];
        target.visible = nextVisible;
      });
      if (prevVisible !== nextVisible) {
        notifyFieldSubscriber(fieldName);
        notifyFormSubscribers();
      }
    },
    setFieldRequiredState: (fieldName, nextRequired) => {
      const log = getLogger();
      log("setFieldRequiredState", fieldName, nextRequired);
      const field = tryToCreateField(fieldName);
      const prevRequired = field.required;
      store.fields = produce(store.fields, (draft) => {
        const target = draft[fieldName];
        target.required = nextRequired;
        if (prevRequired !== nextRequired) {
          target.status = "indeterminate";
        }
      });
      if (prevRequired !== nextRequired) {
        notifyFieldSubscriber(fieldName);
        refreshFormStatus();
        notifyFormSubscribers();
      }
    }
  };
  return fieldApi;
}
export {
  createFieldApi
};
//# sourceMappingURL=createFieldApi.js.map
